import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import bindMethods from 'auto-bind';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons';

import { XOS_RED } from '../../constants/colors';
import {
  ERR_DUPLICATE_NAMES, ERR_BLANK_FIELDS, VALID_INTEGERS, ERR_GROUP_SELECT, ERR_NO_FILTERS,
} from '../../constants/errors';
import { DOWN_DISTANCE_MAP, DOWN_DISTANCE_CODE } from '../../constants/filters';
import { updateDownDistance } from '../../state/filters';
import { DOWN_AND_DISTANCE_KEY, getFilters } from '../../state/selectors/filters';
import { setError } from '../../state/settings';


const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: .75rem 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid transparent;
  border-radius: .25rem;
  color: #c94141;
  background-color: #f8d7da;
`;

const DeleteTableCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${XOS_RED};
`;

class DownDistanceSettings extends PureComponent {
  constructor(props) {
    super(props);
    bindMethods(this);
  }

  getCellType(cell) {
    const { filters } = this.props;
    const isValid = Object.keys(filters[cell.index]).length > 0;
    if (!isValid) return cell.column.Header === 'Name' ? 'New Filter' : '';
    return filters[cell.index][DOWN_DISTANCE_MAP[cell.column.Header]];
  }

  toggleError(status, location, message) {
    const { setError } = this.props;
    setError({
      status,
      location,
      message,
    });
  }

  handleRowEdits(state, rowInfo) {
    const { filters, settings } = this.props;
    const { status } = settings.error;
    const newFilterData = state.data[filters.length - 1];
    const isJustAdded = Object.keys(newFilterData).length === 0;
    const isCurrentlyEdited = rowInfo.index === filters.length;
    const isNoErrorAndBlank = !status
      && (!newFilterData.name || !newFilterData.operand1Value || !newFilterData.operand2Min);

    const areRequiredComplete = newFilterData.name
      && !!newFilterData.operand1Value && !!newFilterData.operand2Min;

    if (isJustAdded) return {};

    // if there is no error and fields are blank (on first error to prevent stack error)
    if (isNoErrorAndBlank && isCurrentlyEdited) {
      this.toggleError(true, DOWN_AND_DISTANCE_KEY, ERR_BLANK_FIELDS);
      return {};
    }

    if (areRequiredComplete && !newFilterData.filterFormulaId) {
      const index = filters.length - 1;
      filters[index] = {
        ...filters[index],
        operand2Max: filters[index].operand2Max ? filters[index].operand2Max : 'max',
        filterFormulaId: DOWN_DISTANCE_CODE,
      };
      this.handleUpdate(filters);
    }
    return {};
  }

  deleteFilter(cell) {
    const itemToDelete = cell.original;
    const { filters } = this.props;
    const filteredDownDistanceFilters = filters.filter(filter => filter.name !== itemToDelete.name);
    this.handleUpdate(filteredDownDistanceFilters);
  }

  handleUpdate(filters) {
    const { groupId, updateDownDistance } = this.props;
    updateDownDistance(DOWN_DISTANCE_CODE, groupId, filters);
    this.toggleError(false, null, '');
  }

  renderEditable(cell) {
    const { filters } = this.props;
    const data = filters;
    const field = DOWN_DISTANCE_MAP[cell.column.Header];

    return (
      <div
        style={{ backgroundColor: '#fafafa' }}
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => {
          const isMaxDistance = cell.column.Header === 'Max Distance';
          let input = e.target.innerText;

          // check for duplicate name
          if (cell.column.Header === 'Name' && data.find(f => f.name === input)) {
            // eslint-disable-next-line prefer-destructuring
            const name = data.filter(f => f[field] === cell.original.name)[0].name;

            // eslint-disable-next-line no-return-assign
            if (name) {
              e.target.innerHTML = name;
              return;
            }

            e.target.innerHTML = '';
            this.toggleError(true, DOWN_AND_DISTANCE_KEY, ERR_DUPLICATE_NAMES);
            return;
          }

          if (isMaxDistance && input === '') input = 'max';

          // if Down and Min Distance are not numbers
          // eslint-disable-next-line no-restricted-globals
          const invalidNumber = isNaN(+input);
          if ((cell.column.Header === 'Min Distance' || cell.column.Header === 'Down') && invalidNumber) {
            // eslint-disable-next-line consistent-return
            return this.toggleError(true, DOWN_AND_DISTANCE_KEY, VALID_INTEGERS);
          }

          const filters = data.map((filter) => {
            if (filter[field] === data[cell.index][field] && cell.original.name === filter.name) {
              return {
                ...filter,
                [field]: input,
              };
            }
            return filter;
          });
          this.handleUpdate(filters);
        }}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: this.getCellType(cell),
        }}
      />
    );
  }

  render() {
    const { filters, groupId, settings } = this.props;
    const { status, location, message } = settings.error;

    if (!groupId) return <ErrorContainer>{ERR_GROUP_SELECT}</ErrorContainer>;

    if (!filters.length > 0) return <ErrorContainer>{ERR_NO_FILTERS}</ErrorContainer>;

    return (
      <React.Fragment>
        {status && location === DOWN_AND_DISTANCE_KEY && <ErrorContainer>{`Error: ${message}`}</ErrorContainer>}
        <ReactTable
          resizable={false}
          showPagination={false}
          getTrProps={(state, rowInfo) => this.handleRowEdits(state, rowInfo)}
          getTdProps={() => ({
            style: {
              textAlign: 'center',
            },
          }
          )}
          data={filters}
          columns={[
            {
              Header: 'Name',
              Cell: this.renderEditable,
              height: '100',
            },
            {
              Header: 'Down',
              Cell: this.renderEditable,
              height: '100',
            },
            {
              Header: 'Min Distance',
              Cell: this.renderEditable,
              height: '100',
            },
            {
              Header: 'Max Distance',
              Cell: this.renderEditable,
              height: '100',
            },
            {
              Header: '',
              Cell: cell => (
                <DeleteTableCell>
                  <FontAwesomeIcon icon={faTimesCircle} onClick={() => this.deleteFilter(cell)} />
                </DeleteTableCell>
              ),
            },
          ]}
          pageSize={filters.length}
          className="-striped -highlight"
        />
      </React.Fragment>
    );
  }
}

DownDistanceSettings.propTypes = {
  groupId: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
  filters: PropTypes.array.isRequired,
  setError: PropTypes.func.isRequired,
  settings: PropTypes.object.isRequired,
  updateDownDistance: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  filters: getFilters(DOWN_AND_DISTANCE_KEY, state.filters),
  settings: state.settings,
  groupId: state.navigationSelection.group.groupId,
});

const mapDispatchToProps = {
  updateDownDistance,
  setError,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(DownDistanceSettings);
